import { useCallback } from 'react';
import { RunLogEntry } from '../types';
import { parseRunLogs } from '../utils/runLog';
import { newRecordCategory } from '../utils/stats';
import { usePersistentState } from './usePersistentState';

import type { Options as ConfettiOptions } from 'canvas-confetti';

// Confetti wordt pas geladen als het nodig is, zodat de eerste weergave sneller klaar is.
async function celebrate(options: ConfettiOptions) {
  try {
    const { default: confetti } = await import('canvas-confetti');
    confetti(options);
  } catch {
    // Een mislukte animatie mag nooit het opslaan verstoren.
  }
}

export interface SavedRun {
  distanceKm: number;
  totalSeconds: number;
  paceSecondsPerKm: number;
  speedKmh: number;
  title?: string;
}

const byDateDesc = (a: RunLogEntry, b: RunLogEntry) => new Date(b.date).getTime() - new Date(a.date).getTime();

/** Het logboek: bewaren, toevoegen, wijzigen, verwijderen (met herstel) en importeren van runs. */
export function useRunLog() {
  const [logs, setLogs] = usePersistentState<RunLogEntry[]>('pace_run_logs', [], parseRunLogs);

  /** Voegt een run toe; geeft de standaardafstand terug als het een nieuw persoonlijk record is. Confetti alleen dan. */
  const insertRun = useCallback((entry: RunLogEntry): string | null => {
    const record = newRecordCategory(entry, logs);
    setLogs((prev) => [entry, ...prev]);
    if (record) {
      celebrate({ particleCount: 90, spread: 70, origin: { y: 0.85 }, colors: ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'] });
    }
    return record;
  }, [logs, setLogs]);

  const saveRun = useCallback((entry: SavedRun) => {
    return insertRun({
      id: 'run_' + Date.now(),
      date: new Date().toISOString(),
      title: entry.title || `${entry.distanceKm} km Run`,
      distanceKm: entry.distanceKm,
      totalSeconds: entry.totalSeconds,
      paceSecondsPerKm: entry.paceSecondsPerKm,
      speedKmh: entry.speedKmh,
      feeling: 4,
    });
  }, [insertRun]);

  const addRun = insertRun;

  const updateRun = useCallback((run: RunLogEntry) => {
    setLogs((prev) => prev.map((r) => (r.id === run.id ? run : r)).sort(byDateDesc));
  }, [setLogs]);

  /** Verwijdert een run en geeft die terug, zodat de aanroeper "ongedaan maken" kan aanbieden. */
  const deleteRun = useCallback((id: string): RunLogEntry | undefined => {
    const removed = logs.find((l) => l.id === id);
    setLogs((prev) => prev.filter((l) => l.id !== id));
    return removed;
  }, [logs, setLogs]);

  const restoreRun = useCallback((run: RunLogEntry) => {
    setLogs((prev) => (prev.some((r) => r.id === run.id) ? prev : [...prev, run].sort(byDateDesc)));
  }, [setLogs]);

  const replaceRuns = useCallback((runs: RunLogEntry[]) => setLogs(runs), [setLogs]);

  return { logs, saveRun, addRun, updateRun, deleteRun, restoreRun, replaceRuns };
}
